import { useCallback, useEffect, useRef, useState } from 'react';
import { getReservationAvailability } from 'api/requests/reservations';

// `month` is a 'YYYY-MM' string. Keeps the previous days/maxPartySize while
// refetching so the UI dims instead of flashing empty.
export default function useReservationAvailability(slug, month, partySize) {
    const [days, setDays] = useState({});
    const [maxPartySize, setMaxPartySize] = useState(null);
    // Which "month|partySize" the current `days` belong to, so callers can ignore stale data mid-fetch.
    const [loadedKey, setLoadedKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const requestId = useRef(0);

    useEffect(() => {
        if (!slug) return undefined;
        const id = ++requestId.current;
        setLoading(true);
        setError(false);

        getReservationAvailability(slug, { month, partySize })
            .then(data => {
                if (id !== requestId.current) return;
                setDays(data?.days || {});
                setLoadedKey(`${month}|${partySize}`);
                if (data?.max_party_size) setMaxPartySize(data.max_party_size);
            })
            .catch(err => {
                if (id !== requestId.current) return;
                console.error('Error fetching reservation availability', err);
                setError(true);
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });

        return () => { requestId.current++; };
    }, [slug, month, partySize, reloadKey]);

    const reload = useCallback(() => setReloadKey(k => k + 1), []);

    return { days, maxPartySize, loadedKey, loading, error, reload };
}
